import { storeToRefs } from "pinia"
import { ODataQueryBuilder } from "@/builder/odata-query-builder"
import { ModelFactory } from "@/factories/model-factory"
import { GenericModelController } from "@/controllers/model-controllers/abstract/generic-model-controller.ts"
import { InstructionFeedbackActivityTemplate } from "@/models/instruction-feedback-activity-template.model.ts"
import { InstructionFeedback } from "@/models/instruction-feedback.model"
import { InstructionFeedbackActivity } from "@/models/instruction-feedback-activity.model"
import type { IInstructionFeedbackActivityTemplate } from "@/interfaces/api/instruction-feedback-activity-template.interface.ts"
import type { IStoreFunctionsConfig } from "@/store/base-functions/base-store-functions.ts"
import { useInstructionFeedbackActivityTemplateStore } from "@/store/instruction-feedback-activity-templates.store.ts"
import { InstructionFeedbackActivityTemplateRepository } from "@/repositories/instruction-feedback-activity-template-repository.ts"
import type { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model.ts"
import type { IRepositoryRequestConfig } from "@/interfaces/repository/repository-request-config.interface.ts"

export class InstructionFeedbackActivityTemplateController extends GenericModelController<
  InstructionFeedbackActivityTemplate,
  IInstructionFeedbackActivityTemplate,
  InstructionFeedbackActivityTemplateRepository
> {
  constructor(storeFunctionsConfig?: IStoreFunctionsConfig<InstructionFeedbackActivityTemplate>) {
    if (!storeFunctionsConfig) {
      const store = useInstructionFeedbackActivityTemplateStore()
      const { instructionFeedbackActivityTemplates } = storeToRefs(store)
      storeFunctionsConfig = {
        managed: false,
        collection: instructionFeedbackActivityTemplates
      }
    }

    super(
      InstructionFeedbackActivityTemplate,
      storeFunctionsConfig,
      new InstructionFeedbackActivityTemplateRepository()
    )
  }

  public getListForKnownBusinessEntityContact(kbec: KnownBusinessEntityContact) {
    const query = new ODataQueryBuilder().eq("owner_id", kbec.id).deep()
    return this.getList({ query })
  }

  public async createInstructionFeedbackActivity(
    templateId: string,
    instructionFeedback: InstructionFeedback,
    requestConfig?: IRepositoryRequestConfig
  ): Promise<InstructionFeedbackActivity | undefined> {
    const response = await this.apiFunctions
      .getRepository()
      .createInstructionFeedbackActivity(templateId, instructionFeedback.id, requestConfig)

    if (response?.status !== 201 || !response.data?.id) {
      return undefined
    }

    return ModelFactory.createModel(InstructionFeedbackActivity, response.data, false)
  }
}
